/**
 * Press quotes slider.
 */
class PressQuotesSlider extends HTMLElement {
    connectedCallback() {
        this.track = this.querySelector('.press-quotes__track');
        this.slides = Array.from(this.querySelectorAll('.press-quotes__slide'));
        this.dots = Array.from(this.querySelectorAll('[data-pq-dot]'));
        this.toggleBtn = this.querySelector('[data-pq-toggle]');

        if (!this.track || this.slides.length <= 1) return;

        this.index = 0;
        this.timer = null;
        this.userPaused = false;

        const delaySeconds = parseFloat(this.dataset.delay);
        this.delay = Number.isFinite(delaySeconds) ? delaySeconds * 1000 : 6000;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.prefersReducedMotion;

        this.bindEvents();
        this.observeSlides();

        if (this.autoplayEnabled) {
            this.start();
        } else if (this.toggleBtn) {
            this.toggleBtn.hidden = true;
        }
    }

    disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
        if (this.handleVisibility) {
            document.removeEventListener('visibilitychange', this.handleVisibility);
        }
    }

    bindEvents() {
        this.dots.forEach((dot) => {
            dot.addEventListener('click', () => {
                this.goTo(parseInt(dot.dataset.pqDot, 10));
                this.pauseByUser(true);
            });
        });

        if (this.toggleBtn) {
            this.toggleBtn.addEventListener('click', () => {
                this.pauseByUser(!this.userPaused);
            });
        }

        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this.resume();
        });

        this.handleVisibility = () => {
            if (document.hidden) {
                this.stop();
            } else {
                this.resume();
            }
        };
        document.addEventListener('visibilitychange', this.handleVisibility);

        this.addEventListener('shopify:block:select', (event) => {
            const slide = event.target.closest('.press-quotes__slide');
            if (slide) this.goTo(this.slides.indexOf(slide));
            this.stop();
        });
        this.addEventListener('shopify:block:deselect', () => this.resume());
    }

    observeSlides() {
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                        this.index = this.slides.indexOf(entry.target);
                        this.updateDots();
                    }
                });
            },
            { root: this.track, threshold: [0.6] }
        );

        this.slides.forEach((slide) => this.observer.observe(slide));
    }

    updateDots() {
        this.dots.forEach((dot, i) => {
            const isActive = i === this.index;
            dot.classList.toggle('press-quotes__dot--active', isActive);
            if (isActive) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });
    }

    goTo(index) {
        if (index < 0 || index >= this.slides.length) return;
        this.track.scrollTo({
            left: this.slides[index].offsetLeft,
            behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
    }

    next() {
        this.goTo((this.index + 1) % this.slides.length);
    }

    start() {
        if (!this.autoplayEnabled || this.userPaused || this.timer) return;
        this.timer = setInterval(() => this.next(), this.delay);
    }

    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    resume() {
        if (document.hidden) return;
        this.start();
    }

    pauseByUser(paused) {
        this.userPaused = paused;

        if (paused) {
            this.stop();
        } else {
            this.start();
        }

        if (this.toggleBtn) {
            this.toggleBtn.setAttribute('aria-pressed', String(paused));
            const label = paused ? 'Play slideshow' : 'Pause slideshow';
            this.toggleBtn.setAttribute('aria-label', label);
            this.toggleBtn.textContent = label;
        }
    }
}

if (!customElements.get('press-quotes-slider')) {
    customElements.define('press-quotes-slider', PressQuotesSlider);
}